/**
 * ApprovalsPage
 *
 * Displays and manages practitioner vacation/time-off requests for a clinic.
 *
 * API endpoints:
 *   GET  /api/practitioner-vacations/?clinic=<id>&status=requests
 *        → returns records (one per request) where date_ranges contains at least one
 *          entry with a non-null status (pending/approved/denied). Legacy null-status entries
 *          are excluded from display via client-side filtering.
 *   PATCH /api/practitioner-vacations/<id>/
 *        → approves/denies that one request: { status, admin_note }
 *
 * Scheduling / vacation data read:
 *   - date_ranges[].start_date / end_date — displayed as a formatted date range
 *   - date_ranges[].leave_type  — "vacation" | "sick" | "personal" | "unpaid"
 *   - date_ranges[].status      — drives filter tabs and the approve/deny action buttons
 *   - date_ranges[].admin_note  — written by the approver; shown read-only after a decision
 *   - date_ranges[].reason      — employee-supplied; shown in the request card body
 *
 * Key fields: id, profile_name, clinic, date_ranges (single entry, embedded approval metadata)
 */
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePractitionerVacations, useUpdatePractitionerVacation } from "@api/hooks";
import { useAuth } from "@api/useAuth";
import { canManagePractitionerVacations } from "@utils";
import { Badge, Button, Card, DataStateHandler, showSuccess } from "@common";

// ── Helpers ────────────────────────────────────────────────────────────────────

const LEAVE_TYPE_LABELS = {
  vacation: "Vacation",
  sick: "Sick",
  personal: "Personal",
  unpaid: "Unpaid Leave",
};

const STATUS_STYLES = {
  pending: "bg-palette-amber-muted text-palette-amber-heading",
  approved: "bg-palette-green-muted text-palette-green-hover",
  denied: "bg-palette-red-muted text-palette-red-heading",
};

const FILTER_TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "denied", label: "Denied" },
  { key: "all", label: "All" },
];

function getInitials(name) {
  return (
    (name || "")
      .replace(/^dr\.?\s*/i, "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("") || "?"
  );
}

function formatApprovalDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function daysBetween(start, end) {
  if (!start || !end) return 0;
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function ApprovalsPage() {
  const { clinicId } = useParams();
  const navigate = useNavigate();

  const { user } = useAuth();
  // Organizer (1), admin (2), area manager (3), practitioner lead (4) can approve or
  // deny. Mirrors backend can_manage_practitioner_vacations; the backend confines
  // roles 3/4 to the requests of their own clinics.
  const canApprove = canManagePractitionerVacations(user);

  const [filter, setFilter] = useState("pending");
  const [notes, setNotes] = useState({});

  // status="requests" tells the backend to return only records that have at least one
  // self-serve range (pending/approved/denied). The backend uses date_ranges__contains
  // filtering; a returned record may still contain legacy null-status entries mixed in,
  // so we filter those out in the flatMap below.
  const { data, isLoading } = usePractitionerVacations({ clinic: clinicId, status: "requests" });
  const allRecords = data?.results ?? [];

  // One row = one request, so each record yields one card. The flatMap is kept because
  // it also tolerates any legacy row that still holds several ranges.
  // Only include entries with a real status — null-status entries are legacy admin records
  // that never went through the self-serve flow and don't belong on this page.
  // noteKey (recordId-rangeIndex) is stable and unique, used as both the React key
  // and the notes draft key so typing in one card never clobbers another.
  const allItems = allRecords.flatMap((v) =>
    (v.date_ranges || [])
      .map((range, idx) => ({ range, idx }))
      .filter(({ range }) => range.status != null) // exclude legacy null-status entries
      .map(({ range, idx }) => ({
        recordId: v.id,
        rangeIndex: idx, // index within the row; 0 for every current record
        noteKey: `${v.id}-${idx}`,
        practitionerName: v.profile_name || `Profile #${v.profile}`,
        clinic: v.clinic || clinicId,
        status: range.status,
        leaveType: range.leave_type,
        reason: range.reason,
        adminNote: range.admin_note,
        startDate: range.start_date,
        endDate: range.end_date,
        // Stored by the backend at submission time; null for legacy entries submitted before this feature.
        vacationDays: range.vacation_days ?? null,
        holidayCount: range.holiday_count ?? null,
        calendarDays: range.calendar_days ?? null,
      })),
  );

  const pendingCount = allItems.filter((i) => i.status === "pending").length;
  const approvedCount = allItems.filter((i) => i.status === "approved").length;
  const deniedCount = allItems.filter((i) => i.status === "denied").length;

  const displayed = filter === "all" ? allItems : allItems.filter((i) => i.status === filter);

  const updateMut = useUpdatePractitionerVacation();

  async function handleAction(recordId, rangeIndex, noteKey, newStatus) {
    try {
      await updateMut.mutateAsync({
        id: recordId,
        // The record IS the request, so no range_index is needed. It is still sent for a
        // legacy row that holds several ranges, where the backend needs it to pick one.
        data: {
          status: newStatus,
          admin_note: notes[noteKey] ?? "",
          ...(rangeIndex > 0 ? { range_index: rangeIndex } : {}),
        },
      });
      setNotes((prev) => {
        const next = { ...prev };
        delete next[noteKey];
        return next;
      });
      showSuccess(newStatus === "approved" ? "Approved successfully." : "Denied successfully.");
    } catch {
      // silent — the hook will surface errors if needed
    }
  }

  return (
    <div className="min-h-screen bg-palette-gray-soft">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-palette-gray-divider px-6 py-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            <Button
              variant="link"
              onClick={() => navigate(`/report/${clinicId}/schedule`)}
              className="text-palette-gray-placeholder hover:text-palette-slate-badge text-sm"
            >
              ← Back
            </Button>
            <div>
              <h1 className="text-lg font-bold text-palette-slate-text">Vacation approvals</h1>
              <p className="text-xs text-palette-gray-secondary">
                Review and decide time-off requests
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`/report/${clinicId}/schedule/vacation-request`)}
            className="px-4 py-2 rounded-xl font-semibold gap-1.5"
          >
            + New request
          </Button>
        </div>
      </div>

      <div className="px-6 py-6 w-full">
        {/* ── Stats ───────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: "Pending", value: pendingCount, color: "text-palette-amber-label" },
            { label: "Approved", value: approvedCount, color: "text-palette-green-badge" },
            { label: "Denied", value: deniedCount, color: "text-palette-red-label" },
          ].map(({ label, value, color }) => (
            <div
              key={label}
              className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm p-5"
            >
              <p className="text-xs font-semibold text-palette-gray-placeholder uppercase tracking-widest mb-2">
                {label}
              </p>
              <p className={`text-4xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* ── Filter tabs ─────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mb-6">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-palette-gray-placeholder shrink-0"
          >
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          <div className="flex items-center gap-1.5">
            {FILTER_TABS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  filter === key
                    ? "bg-palette-blue-badge text-white"
                    : "bg-white border border-palette-gray-divider text-palette-slate-badge hover:bg-palette-gray-soft"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Request list ────────────────────────────────────────────────── */}
        <DataStateHandler
          isLoading={isLoading}
          isEmpty={displayed.length === 0}
          emptyMessage={`No ${filter === "all" ? "" : filter + " "}requests found.`}
          loadingMessage="Loading requests…"
        >
          <div className="space-y-4">
            {displayed.map((item) => {
              const days = daysBetween(item.startDate, item.endDate);
              const leaveLabel = LEAVE_TYPE_LABELS[item.leaveType] || "Vacation";
              const statusStyle =
                STATUS_STYLES[item.status] || "bg-palette-gray-subtle text-palette-gray-badge";
              const statusLabel = item.status
                ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
                : "—";

              return (
                <Card
                  key={item.noteKey}
                  className="shadow-sm border border-palette-gray-subtle overflow-hidden"
                  bodyClassName="p-5"
                >
                  {/* Name + badges */}
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-palette-blue-muted text-palette-blue-heading flex items-center justify-center text-sm font-bold shrink-0">
                      {getInitials(item.practitionerName)}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-palette-slate-text">
                        {item.practitionerName}
                      </span>
                      <Badge className="font-semibold bg-palette-blue-soft text-palette-blue-heading">
                        {leaveLabel}
                      </Badge>
                      <Badge className={`font-semibold ${statusStyle}`}>{statusLabel}</Badge>
                    </div>
                  </div>

                  {/* Date range */}
                  <p className="text-xs text-palette-gray-secondary mb-3 pl-[52px]">
                    {item.clinic}
                    {item.startDate && (
                      <>
                        {" · "}
                        {formatApprovalDate(item.startDate)} → {formatApprovalDate(item.endDate)}
                        {" · "}
                        {/* Show vacation days (holidays excluded) when the backend stored the breakdown;
                            fall back to raw calendar days for legacy entries. */}
                        {item.vacationDays != null && item.holidayCount > 0 ? (
                          <>
                            {item.vacationDays} vacation day{item.vacationDays !== 1 ? "s" : ""}
                            <span className="text-palette-gray-placeholder">
                              {" "}
                              ({item.calendarDays} cal. − {item.holidayCount} holiday
                              {item.holidayCount !== 1 ? "s" : ""})
                            </span>
                          </>
                        ) : (
                          <>
                            {days} day{days !== 1 ? "s" : ""}
                          </>
                        )}
                      </>
                    )}
                  </p>

                  {/* Reason */}
                  {item.reason && (
                    <div className="bg-palette-gray-soft rounded-xl px-4 py-3 mb-4 ml-[52px]">
                      <p className="text-sm text-palette-slate-badge">{item.reason}</p>
                    </div>
                  )}

                  {/* Admin note (resolved requests) */}
                  {item.status !== "pending" && item.adminNote && (
                    <p className="text-xs text-palette-gray-placeholder italic ml-[52px] mb-2">
                      Note: {item.adminNote}
                    </p>
                  )}

                  {/* Approve / Deny actions — roles 1, 2, 3, 4 */}
                  {item.status === "pending" && canApprove && (
                    <div className="flex items-center gap-3 mt-3">
                      <input
                        type="text"
                        value={notes[item.noteKey] ?? ""}
                        onChange={(e) =>
                          setNotes((prev) => ({ ...prev, [item.noteKey]: e.target.value }))
                        }
                        placeholder="Optional note for the employee…"
                        className="flex-1 rounded-xl border border-palette-gray-divider px-4 py-2.5 text-sm text-palette-slate-heading outline-none focus:ring-2 focus:ring-palette-blue-ring focus:border-palette-blue-indicator"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleAction(item.recordId, item.rangeIndex, item.noteKey, "denied")
                        }
                        disabled={updateMut.isPending}
                        className="px-4 py-2.5 rounded-xl border border-palette-red-border text-palette-red-label hover:bg-palette-red-soft font-semibold gap-1.5 shrink-0"
                      >
                        ✕ Deny
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() =>
                          handleAction(item.recordId, item.rangeIndex, item.noteKey, "approved")
                        }
                        disabled={updateMut.isPending}
                        className="px-4 py-2.5 rounded-xl font-semibold gap-1.5 shrink-0"
                      >
                        ✓ Approve
                      </Button>
                    </div>
                  )}
                  {item.status === "pending" && !canApprove && (
                    <p className="text-xs text-palette-gray-placeholder mt-3 ml-[52px]">
                      Awaiting approval from your manager.
                    </p>
                  )}
                </Card>
              );
            })}
          </div>
        </DataStateHandler>
      </div>
    </div>
  );
}
